// ----------------------------------------------------------------------------
// New church onboarding: church profile → first team → invite code to share.
// Two entry points with the same steps:
//   /create-church       new sign-up, carries an onboarding token. Creates the
//                        church, its Free subscription, then finalizes admin setup.
//   /admin/church-setup  signed-in user with no church. Creates the church and
//                        assigns it to them as Admin.
// Either way the creator is the Admin. Positions are NOT asked for here: people
// who join with the code come in as Members and admins set positions later.
// The signed-in user is only updated when they leave the last step, so the
// RequireNoChurch guard doesn't bounce them off the invite-code step.
// ----------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@app/auth/AuthContext';
import { authApi, saveTokens } from '@app/api/auth';
import { churchesApi, groupsApi, organizationApi } from '@app/api/organization';
import { createFreeSubscription } from '@app/api/subscription';
import type { User } from '@app/types';
import { AuthCard, AuthScreen, LogoDisc } from '@app/components/auth/AuthScreen';
import { Banner, Button, Field, Input, Textarea, cx } from '@app/components/ui';
import { monthDayYear } from '@app/lib/format';
import { logger } from '@app/utils/logger';

type Mode = 'onboarding' | 'session';
const STEPS = ['Your church', 'Your team', 'Invite code'] as const;
export const DEFAULT_TEAM_NAME = 'Worship Team';

interface Profile {
  name: string;
  description: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phone: string;
  website: string;
}

const EMPTY: Profile = { name: '', description: '', street: '', city: '', state: '', zipCode: '', country: 'United States', phone: '', website: '' };
const opt = (v: string) => v.trim() || undefined;

function Progress({ step }: { step: number }) {
  return (
    <div className="w-full mb-[26px]" aria-label={`Step ${step + 1} of ${STEPS.length}: ${STEPS[step]}`} role="group">
      <div className="flex justify-between text-[12.5px] mb-2.5">
        <span className="text-t4">
          Step {step + 1} of {STEPS.length}
        </span>
        <span className="text-t3 font-medium">{STEPS[step]}</span>
      </div>
      <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${STEPS.length},1fr)` }}>
        {STEPS.map((s, i) => (
          <div key={s} className="h-1 rounded-full bg-fill2 overflow-hidden">
            <div className="h-full rounded-full bg-acc" style={{ width: i <= step ? '100%' : '0%', transition: 'width .6s var(--ease)' }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function ChurchOnboarding({ mode }: { mode: Mode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useAuth();
  const state = location.state as { onboardingToken?: string; userId?: string } | null;
  const checked = useRef(false);
  const [step, setStep] = useState(0);
  const [profile, setProfile] = useState<Profile>(EMPTY);
  const [showMore, setShowMore] = useState(false);
  const [teamName, setTeamName] = useState(DEFAULT_TEAM_NAME);
  const [teamDesc, setTeamDesc] = useState('');
  const [errors, setErrors] = useState<{ name?: string; team?: string; form?: string }>({});
  const [shake, setShake] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ user: User; churchName: string; code?: string; expiresAt?: string; codeError?: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  // New sign-ups need an onboarding token.
  useEffect(() => {
    if (mode !== 'onboarding' || checked.current) return;
    checked.current = true;
    if (!(state?.onboardingToken || localStorage.getItem('onboarding_token'))) {
      logger.warn('No onboarding token found, redirecting to login');
      navigate('/login', { state: { error: 'Session expired. Please login again.' } });
    }
  }, [mode, state, navigate]);

  const set = (k: keyof Profile, v: string) => {
    setProfile((p) => ({ ...p, [k]: v }));
    if (k === 'name') setErrors((e) => ({ ...e, name: undefined }));
  };
  const fail = (e: typeof errors) => {
    setErrors(e);
    setShake((n) => n + 1);
  };

  const next = (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile.name.trim()) return fail({ name: 'Your church needs a name' });
    setErrors({});
    setStep(1);
  };

  // Creates the church (and for new sign-ups the Free plan + admin session),
  // then the first team and its invite code. Team and code failures don't
  // block: the church exists, and both can be made later from the app.
  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!teamName.trim()) return fail({ team: 'Name your team' });
    setErrors({});
    setBusy(true);
    const request = {
      name: profile.name.trim(),
      description: opt(profile.description),
      address: { street: opt(profile.street), city: opt(profile.city), state: opt(profile.state), zipCode: opt(profile.zipCode), country: opt(profile.country) },
      contactInfo: { phone: opt(profile.phone), website: opt(profile.website) },
    };
    let signedIn: User;
    let churchId: string;
    try {
      if (mode === 'onboarding') {
        const token = state?.onboardingToken || localStorage.getItem('onboarding_token');
        if (!token) throw new Error('No onboarding token available');
        const userId = state?.userId || localStorage.getItem('onboarding_userId') || '';
        const church = await organizationApi.createChurch({ ...request, adminUserId: userId }, token);
        churchId = church.id;
        try {
          await createFreeSubscription(church.id, token);
        } catch (subErr) {
          logger.warn('Failed to create free subscription (non-blocking):', subErr);
        }
        const res = await authApi.finalizeAdminSetup(church.id, token);
        saveTokens(res.token, typeof res.refreshToken === 'string' ? res.refreshToken : res.refreshToken?.token || '');
        signedIn = { id: res.id, username: res.name, email: res.email, userRole: res.userRole, churchId: res.churchId, userLogo: res.userLogo };
        localStorage.setItem('auth_user', JSON.stringify(signedIn));
        localStorage.removeItem('onboarding_token');
        localStorage.removeItem('onboarding_userId');
      } else {
        if (!user) return;
        const church = await churchesApi.create({ ...request, adminUserId: user.id });
        churchId = church.id;
        await authApi.updateUserChurch(user.id, church.id, 'Admin');
        signedIn = { ...user, churchId: church.id, userRole: 'Admin' };
        localStorage.setItem('auth_user', JSON.stringify(signedIn));
      }
    } catch (err: any) {
      logger.error('Error creating church', err);
      setBusy(false);
      if (mode === 'onboarding' && (err?.response?.status === 401 || err?.response?.status === 403)) {
        navigate('/login', { state: { error: 'Session expired or unauthorized. Please login again.' } });
        return;
      }
      return fail({ form: err?.response?.data?.error || err?.response?.data?.message || err?.message || "We couldn't create your church. Try again." });
    }

    let code: string | undefined;
    let expiresAt: string | undefined;
    let codeError = false;
    try {
      const group = await groupsApi.create(churchId, {
        churchId,
        name: teamName.trim(),
        description: opt(teamDesc),
        leaderIds: [signedIn.id],
        createdBy: signedIn.id,
      });
      localStorage.setItem('selectedGroupId', group.id);
      const res = await groupsApi.generateInviteCode(group.id, 7);
      code = res.inviteCode;
      expiresAt = res.expiresAt;
    } catch (err) {
      logger.warn('Team or invite code setup failed (non-blocking)', err);
      codeError = true;
    }
    setResult({ user: signedIn, churchName: request.name, code, expiresAt, codeError });
    setBusy(false);
    setStep(2);
  };

  const finish = (to = '/admin/dashboard') => {
    if (!result) return;
    setUser(result.user);
    navigate(to, { replace: true, state: { message: `Welcome to Worship Planner. ${result.churchName} is ready.` } });
  };

  const copy = async () => {
    if (!result?.code) return;
    try {
      await navigator.clipboard.writeText(result.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* the code stays selectable */
    }
  };

  return (
    <AuthScreen width={520} themeScope="admin">
      <LogoDisc />
      <Progress step={step} />
      {step === 0 && (
        <AuthCard as="form" onSubmit={next} noValidate shake={shake} className="wp-rise">
          <h1 className="text-[26px] font-semibold tracking-[-0.025em]">Tell us about your church</h1>
          <p className="text-[14.5px] text-t3 mt-2 mb-6 leading-[1.6]">This is how your team sees it in invites and rosters.</p>
          <div className="flex flex-col gap-4">
            <Field label="Church name" htmlFor="ob-name" error={errors.name}>
              <Input id="ob-name" height={48} autoFocus value={profile.name} invalid={!!errors.name} placeholder="Grace Community Church" onChange={(e) => set('name', e.target.value)} />
            </Field>
            <Field label="City" optional htmlFor="ob-city">
              <Input id="ob-city" height={48} value={profile.city} onChange={(e) => set('city', e.target.value)} />
            </Field>
            {showMore ? (
              <div className="flex flex-col gap-4 wp-fade">
                <Field label="Description" optional htmlFor="ob-desc">
                  <Textarea id="ob-desc" rows={2} value={profile.description} onChange={(e) => set('description', e.target.value)} />
                </Field>
                <Field label="Street" optional htmlFor="ob-street">
                  <Input id="ob-street" value={profile.street} onChange={(e) => set('street', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="State or region" optional htmlFor="ob-state">
                    <Input id="ob-state" value={profile.state} onChange={(e) => set('state', e.target.value)} />
                  </Field>
                  <Field label="Postal code" optional htmlFor="ob-zip">
                    <Input id="ob-zip" mono value={profile.zipCode} onChange={(e) => set('zipCode', e.target.value)} />
                  </Field>
                </div>
                <Field label="Country" optional htmlFor="ob-country">
                  <Input id="ob-country" value={profile.country} onChange={(e) => set('country', e.target.value)} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Phone" optional htmlFor="ob-phone">
                    <Input id="ob-phone" type="tel" value={profile.phone} onChange={(e) => set('phone', e.target.value)} />
                  </Field>
                  <Field label="Website" optional htmlFor="ob-web">
                    <Input id="ob-web" type="url" value={profile.website} onChange={(e) => set('website', e.target.value)} />
                  </Field>
                </div>
              </div>
            ) : (
              <Button variant="link" className="self-start" onClick={() => setShowMore(true)}>
                + Add address and contact details
              </Button>
            )}
          </div>
          <Button type="submit" size="xl" block shape="form" className="mt-6">
            Continue
          </Button>
        </AuthCard>
      )}

      {step === 1 && (
        <AuthCard as="form" onSubmit={create} noValidate shake={shake} className="wp-rise">
          <h1 className="text-[26px] font-semibold tracking-[-0.025em]">Set up your team</h1>
          <p className="text-[14.5px] text-t3 mt-2 mb-6 leading-[1.6]">
            You're the Admin. Everyone who joins with your invite code comes in as a Member, and you can set their positions later from Members.
          </p>
          <div className="flex flex-col gap-4">
            <Field label="Team name" htmlFor="ob-team" error={errors.team}>
              <Input id="ob-team" height={48} autoFocus value={teamName} invalid={!!errors.team} onChange={(e) => setTeamName(e.target.value)} />
            </Field>
            <Field label="What it serves" optional htmlFor="ob-team-desc">
              <Input id="ob-team-desc" value={teamDesc} placeholder="Sunday services" onChange={(e) => setTeamDesc(e.target.value)} />
            </Field>
          </div>
          {errors.form && (
            <Banner tone="acc" role="alert" className="mt-4">
              {errors.form}
            </Banner>
          )}
          <div className="flex gap-2.5 mt-6">
            <Button variant="ghost" shape="form" size="xl" onClick={() => setStep(0)} disabled={busy}>
              Back
            </Button>
            <Button type="submit" shape="form" size="xl" className="flex-1" loading={busy}>
              Create church
            </Button>
          </div>
        </AuthCard>
      )}

      {step === 2 && result && (
        <AuthCard className="wp-rise">
          <div className="w-14 h-14 rounded-full bg-ok-bg text-ok flex items-center justify-center mb-[18px]" aria-hidden="true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <h1 className="text-[26px] font-semibold tracking-[-0.025em]">{result.churchName} is ready</h1>
          {result.code ? (
            <>
              <p className="text-[14.5px] text-t3 mt-2 mb-6 leading-[1.6]">Share this code with your team. You approve each person who joins.</p>
              <label className="wp-label" htmlFor="ob-code">
                Your invite code
              </label>
              <div className="flex gap-2 items-center">
                <output
                  id="ob-code"
                  className="flex-1 h-[52px] rounded-[12px] bg-fill border border-line2 flex items-center px-4 font-mono text-[20px] font-bold tracking-[.1em] select-all"
                >
                  {result.code}
                </output>
                <Button variant="dark" shape="form" className={cx('!h-[52px] !rounded-[12px] !px-[18px]')} onClick={copy}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              {result.expiresAt && <div className="text-[12.5px] text-t4 mt-2">Works until {monthDayYear(result.expiresAt)}. Make new codes any time from Invite codes.</div>}
            </>
          ) : (
            <p className="text-[14.5px] text-t3 mt-2 mb-2 leading-[1.6]">
              Your church is set up. We couldn't make an invite code just now; you can create one from Invite codes.
            </p>
          )}
          <div className="flex flex-col gap-2 mt-6">
            <Button size="xl" block shape="form" onClick={() => finish()}>
              Go to dashboard
            </Button>
            {result.codeError && (
              <Button variant="ghost" size="xl" block shape="form" onClick={() => finish('/admin/invite')}>
                Open Invite codes
              </Button>
            )}
          </div>
        </AuthCard>
      )}
    </AuthScreen>
  );
}

export const CreateChurch: React.FC = () => <ChurchOnboarding mode="onboarding" />;
export const ChurchSetup: React.FC = () => <ChurchOnboarding mode="session" />;
